import React, { useState } from 'react';
import { ChevronDown, ExternalLink, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

// Floating clouds background - matching main site
const FloatingClouds = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      <div className="absolute top-[0%] left-[0%] w-[600px] h-[300px] bg-stone-400/40 rounded-full blur-[100px] animate-cloud-drift" />
      <div className="absolute top-[5%] right-[0%] w-[500px] h-[250px] bg-pink-300/40 rounded-full blur-[90px] animate-cloud-drift-slow" style={{ animationDelay: '2s' }} />
      <div className="absolute top-[20%] left-[5%] w-[550px] h-[280px] bg-blue-300/35 rounded-full blur-[100px] animate-cloud-drift" style={{ animationDelay: '1s' }} />
      <div className="absolute top-[40%] right-[5%] w-[480px] h-[240px] bg-purple-300/40 rounded-full blur-[90px] animate-cloud-drift-slow" style={{ animationDelay: '3s' }} />
      <div className="absolute top-[60%] left-[0%] w-[520px] h-[260px] bg-amber-300/35 rounded-full blur-[100px] animate-cloud-drift" style={{ animationDelay: '4s' }} />
      <div className="absolute top-[80%] right-[0%] w-[560px] h-[280px] bg-teal-300/35 rounded-full blur-[90px] animate-cloud-drift-slow" style={{ animationDelay: '0.5s' }} />
    </div>
  );
};

const MediaKit = () => {
  const [expandedPitch, setExpandedPitch] = useState(null);

  const pitches = [
    {
      id: 1,
      workingTitle: "The Invisible Tech Stack Behind Fashion's Biggest Teams",
      tags: ["operations", "AI tools", "workflow", "creator economy"],
      oneSentence: "This piece maps the real tech stacks behind fashion and beauty teams and shows how AI tools, project software, and community platforms shape what we see\u00A0as\u00A0culture.",
      whyNow: "AI and automation are headline topics but the invisible tools that operators rely on decide how fast campaigns ship, how community feedback is heard, and where burnout shows up.",
      storyFocus: [
        "Day in the life of fashion operators who manage content, ecommerce, and social operations.",
        "The difference between a stack that is intentionally designed versus a patchwork of tools.",
        "How disabled and chronically ill team members experience tool fatigue and context switching."
      ],
      reportingPlan: [
        "Interviews with marketing and product operators across luxury, beauty, and streetwear.",
        "Breakdown of common AI and workflow tools in use.",
        "Data from industry reports on tool bloat and productivity."
      ],
      lens: "I led a 30+ person creative team inside my own startup and now advise founders and creative operators on systems, tools, and operations that protect both output and\u00A0wellbeing."
    },
    {
      id: 2,
      workingTitle: "Designing Fashion Apps for Brains on Low Battery",
      tags: ["accessibility", "UX design", "invisible disability", "ecommerce"],
      oneSentence: "A story on how fashion and beauty platforms can design interfaces for shoppers who are neurodivergent, chronically ill, or managing fatigue and what they stand to\u00A0gain\u00A0by\u00A0doing\u00A0so.",
      whyNow: "Disability and accessibility are often framed as compliance while invisible disabilities are shaping how people actually shop and engage with fashion both online and in store.",
      storyFocus: [
        "What a calm, low-friction shopping flow looks like from homepage to checkout.",
        "Examples of specific interaction patterns that are hostile to low-energy shoppers.",
        "The business impact of reducing cognitive load and sensory overwhelm."
      ],
      reportingPlan: [
        "Interviews with disabled shoppers, UX researchers, and accessibility specialists.",
        "Analysis of specific apps and sites with annotated screens.",
        "Synthesis of existing data on disability prevalence, drop-off rates, and cart abandonment."
      ],
      lens: "My own recent disability has shifted how I move through fashion tech surfaces, which now feel like an accessibility test I cannot turn\u00A0off."
    },
    {
      id: 3,
      workingTitle: "Fashion's Soft Launch Culture",
      tags: ["experimentation", "sustainability", "data-driven", "product strategy"],
      oneSentence: "A reported piece on how brands quietly test products, formats, and aesthetics with small, data-informed releases before going all in, reducing waste while tightening the feedback loop with\u00A0customers.",
      whyNow: "Fashion is under pressure to cut overproduction and meet stricter sustainability targets while still chasing newness. AI, ecommerce, and community tools now make it easier than ever to run small, targeted tests and read real-time reactions before scaling.",
      storyFocus: [
        "Case studies of brands using soft launches: capsule collections, limited online drops, community-only releases, or regional pilots that inform larger buys.",
        "How teams combine quantitative data (sell-through, waitlists, search and save behaviour) with qualitative input (DMs, Discord, surveys, in-store conversations) to decide what graduates from experiment to core line.",
        "The creative upside: designers and merchandisers getting permission to explore weirder ideas because they start as low-risk tests.",
        "The sustainability upside: fewer misses, more accurate buys, and the ability to intentionally produce less of what is not working."
      ],
      reportingPlan: [
        "Interview product, merchandising, and growth leads at a mix of luxury, contemporary, and direct-to-consumer brands that already run structured experiments.",
        "Speak with data and retail analysts who track assortment performance and overproduction across the industry.",
        "Include consumer voices who have experienced being part of early test groups or friends-and-family drops and can speak to how it changed their relationship to the brand.",
        "Pull in industry data on overproduction, returns, and forecast error, plus examples where smaller test buys led to better full-scale investments."
      ],
      lens: "I talk fluently about experimentation and MVP logic from my founder background and connect that mindset to fashion's shift toward more agile, data-informed assortments. I think in systems, so I map how soft launch culture affects design timelines, supply chain decisions, marketing calendars, and community strategy. I can also tie in accessibility and energy: softer, more thoughtful launch cycles that feel better for both teams and customers than constant\u00A0hype."
    }
  ];

  // Main Media Kit Page with futuristic styling
  return (
    <div className="min-h-screen relative overflow-hidden">
      <FloatingClouds />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-white/70 backdrop-blur-xl border-b border-stone-200/50 sticky top-0 z-20">
          <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-1">Private Portfolio</p>
              <div className="flex items-center gap-3">
                <h1 className="text-xl md:text-2xl font-light text-stone-900 tracking-tight">Story Pitches</h1>
                <span className="text-[10px] font-medium text-purple-600 bg-purple-100/80 px-2.5 py-1 rounded-full tracking-wider">fashion x tech</span>
              </div>
            </div>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-800 text-sm transition-colors bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full border border-stone-200/50"
              data-testid="header-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">techbaenae.com</span>
            </Link>
          </div>
        </header>

        <main className="max-w-4xl lg:max-w-5xl mx-auto px-6 py-12">
          {/* Profile Card */}
          <section className="mb-16" data-testid="profile-section">
            <div className="relative">
              {/* Gradient background blur */}
              <div className="absolute inset-0 bg-gradient-to-r from-stone-800 to-stone-900 rounded-3xl blur-xl opacity-30" />
              
              <div className="relative bg-gradient-to-br from-stone-800 via-stone-900 to-stone-800 text-white rounded-3xl p-8 md:p-10 shadow-2xl overflow-hidden">
                {/* Subtle pattern overlay */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute top-0 left-0 w-full h-full" style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                    backgroundSize: '32px 32px'
                  }} />
                </div>
                
                <div className="relative flex flex-col md:flex-row gap-8">
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden border-2 border-white/10 shadow-xl">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_tech-baenae-hub/artifacts/kpf43b8k_headshotXLZOOM%20-%20techbaenae.jpeg"
                        alt="Naomi Thomas"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="mb-5">
                      <h3 className="text-2xl md:text-3xl font-light mb-2">Naomi Thomas</h3>
                      <p className="text-stone-300 text-sm tracking-[0.15em]">@techbaenae</p>
                    </div>
                    
                    <p className="text-stone-200 mb-8 leading-relaxed text-sm md:text-lg font-light">
                      Former Ford Models talent and exited edtech founder exploring how technology, culture, and style influence the way we move through the world.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-6 text-sm">
                      <div>
                        <p className="text-stone-400 uppercase tracking-[0.2em] text-xs mb-3">Focus Areas</p>
                        <p className="text-stone-200 leading-relaxed">AI and fashion commerce • Phygital UX • Invisible disability • Operator tools • Digital&nbsp;identity</p>
                      </div>
                      
                      <div>
                        <p className="text-stone-400 uppercase tracking-[0.2em] text-xs mb-3">Approach</p>
                        <p className="text-stone-200 leading-relaxed">Research-driven reporting • Systems thinking across IRL and URL • Human-centered&nbsp;design</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Pitches */}
          <section data-testid="pitches-section">
            <div className="mb-10">
              <h2 className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Story Drafts</h2>
              <p className="text-stone-700 font-light">{pitches.length} pitches in active development</p>
            </div>

            <div className="space-y-8">
              {pitches.map((pitch, index) => {
                const sectionTitles = [
                  "How teams work (the people + tools)",
                  "How users experience (accessibility + UX)",
                  "How products get made (process + sustainability)"
                ];
                
                return (
                  <div key={pitch.id}>
                    {/* Section Title */}
                    <div className="mb-4">
                      <p className="text-[9px] md:text-[11px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-stone-500 font-medium whitespace-nowrap">
                        {sectionTitles[index]}
                      </p>
                    </div>
                    
                    <div
                      className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/50 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-stone-200/30"
                      data-testid={`pitch-card-${index + 1}`}
                    >
                      {/* Pitch Header */}
                      <button
                        onClick={() => setExpandedPitch(expandedPitch === pitch.id ? null : pitch.id)}
                        className="w-full text-left p-6 md:p-8 flex items-start justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-purple-300/50 focus:ring-inset rounded-2xl"
                        aria-expanded={expandedPitch === pitch.id}
                        aria-controls={`pitch-content-${pitch.id}`}
                        data-testid={`pitch-toggle-${index + 1}`}
                      >
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-4">
                            {pitch.tags.map((tag, tagIndex) => (
                              <span 
                                key={tagIndex}
                                className="text-[10px] font-medium text-purple-600 bg-purple-100/80 px-2.5 py-1 rounded-full tracking-wider"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                          <h3 className="text-[15px] leading-tight md:text-xl md:leading-snug font-light text-stone-900 mb-3">
                            {pitch.workingTitle}
                          </h3>
                          <p className="text-stone-600 text-sm md:text-base leading-relaxed" style={{ textWrap: 'balance' }}>
                            {pitch.oneSentence}
                          </p>
                        </div>
                        <div className="flex-shrink-0 mt-1 p-2 rounded-full bg-stone-100/80 transition-transform duration-300" style={{
                          transform: expandedPitch === pitch.id ? 'rotate(180deg)' : 'rotate(0)'
                        }}>
                          <ChevronDown className="w-5 h-5 text-stone-500" />
                        </div>
                      </button>

                  {/* Expanded Content */}
                  {expandedPitch === pitch.id && (
                    <div 
                      id={`pitch-content-${pitch.id}`}
                      className="px-6 md:px-8 pb-8 border-t border-stone-100/80 pt-6 animate-fadeIn"
                    >
                      <div className="grid md:grid-cols-2 gap-8">
                        <div>
                          <h4 className="text-[10px] uppercase tracking-[0.2em] text-purple-500 mb-3 font-medium">Why It Matters Now</h4>
                          <p className="text-stone-700 text-sm leading-relaxed">{pitch.whyNow}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-[10px] uppercase tracking-[0.2em] text-purple-500 mb-3 font-medium">Story Focus</h4>
                          <ul className="space-y-2">
                            {pitch.storyFocus.map((point, i) => (
                              <li key={i} className="text-stone-700 text-sm leading-relaxed flex items-start gap-3">
                                <span className="text-pink-400 mt-1.5">●</span>
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="text-[10px] uppercase tracking-[0.2em] text-purple-500 mb-3 font-medium">Reporting Plan</h4>
                          <ul className="space-y-2">
                            {pitch.reportingPlan.map((point, i) => (
                              <li key={i} className="text-stone-700 text-sm leading-relaxed flex items-start gap-3">
                                <span className="text-blue-400 mt-1.5">●</span>
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="text-[10px] uppercase tracking-[0.2em] text-purple-500 mb-3 font-medium">My Lens</h4>
                          <p className="text-stone-700 text-sm leading-relaxed italic border-l-2 border-purple-200 pl-4">{pitch.lens}</p>
                        </div>
                      </div>
                    </div>
                  )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Footer */}
          <footer className="mt-20 pt-8 border-t border-stone-200/50">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-stone-500">
              <p className="font-light">This page is unlisted and password protected.</p>
              <div className="flex items-center gap-6">
                <a 
                  href="https://www.linkedin.com/in/naomithomas" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-stone-700 transition-colors"
                  data-testid="linkedin-link"
                >
                  LinkedIn <ExternalLink className="w-3 h-3" />
                </a>
                <span data-testid="email-text">naomi@techbaenae.com</span>
              </div>
            </div>
          </footer>
        </main>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes cloud-drift {
          0%, 100% { transform: translateX(0) translateY(0); }
          50% { transform: translateX(30px) translateY(-20px); }
        }
        @keyframes cloud-drift-slow {
          0%, 100% { transform: translateX(0) translateY(0); }
          50% { transform: translateX(-20px) translateY(15px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-cloud-drift {
          animation: cloud-drift 20s ease-in-out infinite;
        }
        .animate-cloud-drift-slow {
          animation: cloud-drift-slow 25s ease-in-out infinite;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default MediaKit;
